import { StateCreator } from "zustand";

import { ArchiveItem } from "models/ArchiveItem";
import {
  Banner,
  DashboardUpdate,
  UploadedDocument
} from "models/dashboard/dashboard";
import { Thread } from "models/inbox/inbox";
import { RootState } from "store";
import { BannersAPI } from "api/banners";
import { isInternalUser } from "common/MetaActions";
import { ignoreStaleResponse } from "helpers/ignoreStaleResponse";

export interface DashboardSlice {
  threads: Thread[];
  archivedFiles: ArchiveItem[];
  uploadedDocuments: UploadedDocument[];
  banners: Banner[];
  isLoading: boolean;

  getDashboard: (updatedDashboard: DashboardUpdate) => void;
  setDashboardLoading: (isLoading: boolean) => void;
  fetchDashboard: (customerId: string) => void;
  refreshDashboardThreads: (customerId: string) => Promise<void>;
}

export const createDashboardSlice: StateCreator<
  RootState,
  [["zustand/devtools", never]],
  [],
  DashboardSlice
> = (set, get) => ({
  threads: [],
  archivedFiles: [],
  uploadedDocuments: [],
  banners: [],
  isLoading: false,

  getDashboard: (updatedDashboard) =>
    set(
      ({ dashboard }) => ({
        dashboard: {
          ...dashboard,
          ...updatedDashboard
        }
      }),
      false,
      "dashboard/GET_DASHBOARD"
    ),

  setDashboardLoading: (isLoading) =>
    set(
      ({ dashboard }) => ({
        dashboard: {
          ...dashboard,
          isLoading
        }
      }),
      false,
      "dashboard/SET_DASHBOARD_LOADING"
    ),

  fetchDashboard: async (customerId) => {
    set({}, false, "dashboard/FETCH_DASHBOARD");
    const { setUnreadThreadsStatus, setUnhandledUploadsStatus, resetStatus } =
      get().status;
    resetStatus();
    if (!customerId) {
      get().dashboard.setDashboardLoading(false);
      get().error.appendErrorNotification(
        "Obligatoriskt kundnummer saknas.",
        {}
      );
      return;
    }
    get().dashboard.setDashboardLoading(true);
    try {
      const banners = await BannersAPI.getBannersList();
      const threads = (await setUnreadThreadsStatus(customerId)) ?? [];
      const uploadedDocuments = isInternalUser()
        ? ((await setUnhandledUploadsStatus(customerId)) ?? [])
        : [];

      const newResult: DashboardUpdate = {
        threads: threads,
        uploadedDocuments: uploadedDocuments,
        banners: banners
      };
      const activeId = get().global.workingCustomerId;
      if (ignoreStaleResponse(activeId, customerId, "fetchDashboard")) {
        return;
      }
      get().dashboard.getDashboard(newResult);
    } catch (error) {
      get().error.appendErrorNotification(
        "Hämtning av avisering från admin misslyckades.",
        error
      );
    } finally {
      get().dashboard.setDashboardLoading(false);
    }
  },

  refreshDashboardThreads: async (customerId) => {
    set({}, false, "dashboard/REFRESH_DASHBOARD_THREADS");
    if (!customerId) {
      return;
    }
    try {
      const { setUnreadThreadsStatus } = get().status;
      const threads = (await setUnreadThreadsStatus(customerId)) ?? [];

      const activeId = get().global.workingCustomerId;
      if (ignoreStaleResponse(activeId, customerId, "refreshDashboardThreads")) {
        return;
      }

      set(
        ({ dashboard }) => ({
          dashboard: {
            ...dashboard,
            threads: threads
          }
        }),
        false,
        "dashboard/UPDATE_THREADS"
      );
    } catch (error) {
      get().error.appendErrorNotification(
        "Uppdatering av konversationer misslyckades.",
        error
      );
    }
  }
});
